/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2023, Zumper
 */
/** @jsx jsx */

import { Box, Heading } from '@chakra-ui/react'
import { jsx } from '@emotion/react'
import { ExpandableLinkPack, type HandleLinkClickArgs } from '@zcode-ui/web-zumper-ui'
import type { DetailLink } from '@zcode/bizos'
import { HOMEPAGE as HOMEPAGE_SECTIONS } from '@zcode/mixpanel'
import { AnalyticsC } from '@zcode/nr-utils'
import { TOP_TWENTY_FIVE_USA_CITIES } from '@zcode/nr/es/shared/constants/default_city_groups'
import { routesConstants } from '@zcode/zumper-routes'
import { useHomepageImpression } from '../hooks/useHomepageImpression'
import {
  afrList,
  cheapAfrList,
  hfrList,
  luxuryAfrList,
  petFriendlyAfrList,
  shortTermAfrList,
} from './LinkLists'
import { toExpandableLinkObject } from './helpers'

const hfrLinks = hfrList.map(toExpandableLinkObject)
const afrLinks = afrList.map(toExpandableLinkObject)
const cheapAfrLinks = cheapAfrList.map(toExpandableLinkObject)
const shortTermAfrLinks = shortTermAfrList.map(toExpandableLinkObject)
const petFriendlyAfrLinks = petFriendlyAfrList.map(toExpandableLinkObject)
const luxuryAfrLinks = luxuryAfrList.map(toExpandableLinkObject)
const popularCitiesLinks = TOP_TWENTY_FIVE_USA_CITIES.map((item) => ({
  eventName: item.name,
  linkName: item.url,
  title: item.name,
  to: `/${routesConstants.APTS_BASE_URL}/${item.url}`,
}))

export interface HomepageLinksProps {
  addressLinks: readonly DetailLink[]
  buildingLinks: readonly DetailLink[]
  trackLinkClick: (args: HandleLinkClickArgs) => void
}

export const HomepageLinks = ({
  addressLinks = [],
  buildingLinks = [],
  trackLinkClick,
}: HomepageLinksProps) => {
  const ref = useHomepageImpression({
    name: 'LINKPACKS',
    mixpanelSection: HOMEPAGE_SECTIONS.LINKPACKS,
  })
  return (
    <Box<'div'> bg="gray.0" ref={ref}>
      <Box<'div'> margin="0 auto" maxWidth="1440px" pb={14} pt={20} px={{ base: 6 }}>
        <Heading<'h2'> as="h2" fontSize="2xl" mb={6} variant="display28To32">
          Explore rentals in popular cities
        </Heading>
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Houses for rent"
          links={hfrLinks}
        />
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Homes and apartments in popular cities"
          links={popularCitiesLinks}
        />
        {addressLinks.length > 0 && (
          <ExpandableLinkPack
            analyticChannel={AnalyticsC.HOMEPAGE}
            handleLinkClick={trackLinkClick}
            header="Recent on-market addresses"
            links={addressLinks.map(({ displayText, url }) => ({
              eventName: displayText,
              linkName: url,
              title: displayText,
              to: url,
            }))}
          />
        )}
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Apartments for rent"
          links={afrLinks}
        />
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Cheap apartments"
          links={cheapAfrLinks}
        />
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Short term rentals"
          links={shortTermAfrLinks}
        />
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Pet friendly apartments"
          links={petFriendlyAfrLinks}
        />
        <ExpandableLinkPack
          analyticChannel={AnalyticsC.HOMEPAGE}
          handleLinkClick={trackLinkClick}
          header="Luxury apartments"
          links={luxuryAfrLinks}
        />
        {buildingLinks.length > 0 && (
          <ExpandableLinkPack
            analyticChannel={AnalyticsC.HOMEPAGE}
            handleLinkClick={trackLinkClick}
            header="Best apartment building rentals right now"
            links={buildingLinks.map(({ displayText, url }) => ({
              eventName: displayText,
              linkName: url,
              title: displayText,
              to: url,
            }))}
          />
        )}
      </Box>
    </Box>
  )
}
